/**
 * @fileoverview Stationgate Splash Page
 * Cinematic full-viewport intro experience for CAP.IO.
 * This is a splash/intro screen that leads to the main home page.
 * 
 * @module pages/stationgate
 */

import { el } from "../shared/components/simpleRender.js";

/**
 * Taglines - one is randomly selected on page load.
 * @type {string[]}
 */
const TAGLINES = [
  "Welcome to the end of the line. The markets disembark here.",
  "Everyone's brave until the candle goes out.",
  "Next stop: Liquidity crisis.",
  "Hope is not a hedge.",
  "Smart money exits left. Dumb money stays on the train.",
  "Where price meets fate.",
  "All aboard the macro bloodbath express.",
  "If you think this time is different, you're already on the train."
];

/**
 * Flag to track if parallax is enabled.
 * @type {boolean}
 */
let parallaxEnabled = true;

/**
 * Reference to mousemove handler for cleanup.
 * @type {function|null}
 */
let mouseMoveHandler = null;

/**
 * Reference to mouseleave handler for cleanup.
 * @type {function|null}
 */
let mouseLeaveHandler = null;

/**
 * Renders the Stationgate splash page.
 * @returns {HTMLElement}
 */
export function renderStationgatePage() {
  // Select random tagline
  const randomIndex = Math.floor(Math.random() * TAGLINES.length);
  const selectedTagline = TAGLINES[randomIndex];

  return el("div", { className: "station-gate-wrapper" }, [
    // Background Image Layer (parallax enabled)
    el("div", { className: "background-layer", id: "background-layer" }, []),

    // Lantern Glow Effect
    el("div", { className: "lantern-glow", id: "lantern-glow" }, []),

    // Fog Overlay Layers
    el("div", { className: "fog-layer fog-layer-1" }, []),
    el("div", { className: "fog-layer fog-layer-2" }, []),

    // Vignette Overlay
    el("div", { className: "vignette-overlay" }, []),

    // Main Content
    el("main", { className: "station-gate", id: "main-content", tabindex: "-1" }, [
      el("div", { className: "content-wrapper" }, [
        // Site Label
        el("div", { className: "site-label" }, ["CAPITULATORS.IO"]),

        // Main Tagline (randomly selected)
        el("h1", { className: "main-tagline", id: "main-tagline" }, [selectedTagline]),

        // Subtitle
        el("p", { className: "subtitle", id: "subtitle" }, [
          "Capitulators.io is the definitive destination for tracking global liquidity stress."
        ]),

        // Enter Button
        el("div", { className: "button-container" }, [
          el("a", { 
            href: "#/home",
            className: "enter-button",
            id: "enter-button"
          }, [
            el("span", { className: "button-fog" }, []),
            el("span", { className: "button-text" }, ["ENTER THE STATION"]),
          ]),
        ]),

        // Disclaimer
        el("p", { className: "disclaimer" }, [
          "Past capitulation is no guarantee of future capitulation. But let's be honest, it probably is."
        ]),
      ]),
    ]),
  ]);
}

/**
 * Sets up parallax mouse movement effect.
 */
function setupParallax() {
  const backgroundLayer = document.querySelector(".background-layer");
  const lanternGlow = document.querySelector(".lantern-glow");

  if (!backgroundLayer) return;

  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (prefersReducedMotion) {
    parallaxEnabled = false;
    return;
  }

  const intensity = 0.02;
  const lanternIntensity = 0.03;

  mouseMoveHandler = (e) => {
    if (!parallaxEnabled) return;

    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    const deltaX = (e.clientX - centerX) / centerX;
    const deltaY = (e.clientY - centerY) / centerY;

    const bgX = -deltaX * intensity * 100;
    const bgY = -deltaY * intensity * 100;
    backgroundLayer.style.transform = `translate(${bgX}px, ${bgY}px) scale(1.1)`;

    if (lanternGlow) {
      const glowX = -deltaX * lanternIntensity * 100;
      const glowY = -deltaY * lanternIntensity * 100;
      lanternGlow.style.transform = `translate(${glowX}px, ${glowY}px)`;
    }
  };

  mouseLeaveHandler = () => {
    backgroundLayer.style.transform = "translate(0, 0) scale(1.1)";
    if (lanternGlow) {
      lanternGlow.style.transform = "translate(0, 0)";
    }
  };

  document.addEventListener("mousemove", mouseMoveHandler);
  document.addEventListener("mouseleave", mouseLeaveHandler);
}

/**
 * Cleans up parallax event listeners.
 */
function cleanupParallax() {
  if (mouseMoveHandler) {
    document.removeEventListener("mousemove", mouseMoveHandler);
    mouseMoveHandler = null;
  }
  if (mouseLeaveHandler) {
    document.removeEventListener("mouseleave", mouseLeaveHandler);
    mouseLeaveHandler = null;
  }
}

/**
 * Reveals the tagline with animation.
 */
function revealTagline() {
  const taglineEl = document.querySelector(".main-tagline");
  if (!taglineEl) return;

  setTimeout(() => {
    taglineEl.classList.add("revealed");
  }, 800);

  taglineEl.classList.add("typing");
  setTimeout(() => {
    taglineEl.classList.remove("typing");
  }, 2500);
}

/**
 * Initializes stationgate effects after DOM insertion.
 */
export function initStationgateEffects() {
  setupParallax();
  revealTagline();

  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (prefersReducedMotion) {
    const tagline = document.querySelector(".main-tagline");
    if (tagline) {
      tagline.classList.add("revealed");
      tagline.classList.remove("typing");
    }
  }
}

/**
 * Cleans up stationgate effects.
 */
export function cleanupStationgate() {
  cleanupParallax();
}

export { TAGLINES };
